import { useCallback, useMemo, useRef, useState } from "react";
import { useFormik, FormikProvider } from "formik";
import z from "zod";
import { CloseTemplateBasis } from "@common/types/closeTemplateBasis";
import type { OrganizationTaskTemplate } from "@common/types/organizationTaskTemplate";
import { OrganizationCloseStage } from "@common/types/organizationCloseStage";
import { TaskType } from "@common/types/task";
import {
    TaskTemplateAssigneeRole,
    TaskTemplateDeadlineAnchor,
} from "@common/types/taskTemplate";
import { validateFormikWithZodSchema } from "@helpers/form";
import { useAutofocus } from "@hooks/useAutofocus";
import { TaskDrawerHeaderFields } from "@components/drawers/TaskDrawerHeaderFields";
import { TaskTypeField } from "./TaskTypeField";
import { TaskTypePicker } from "./TaskTypePicker";
import {
    getTaskTypeDefaults,
    resolveTaskTypeFromOption,
    type TaskTypeOption,
} from "./closeTemplateTasksDrawer.constants";
import { toDrawerDefaultsFromOrgTaskTemplate } from "./orgTaskTemplatePicker.utils";
import { createDefaultViewConfigForTaskTypeOption } from "./taskTypeOptionViewConfig";
import {
    DEFAULT_TRANSACTIONS_VIEW_TYPE,
    readReviewFieldsFromViewConfig,
    type TransactionsViewType,
    withTransactionsViewType,
} from "./closeTemplateTaskViewConfig";
import type { LocalTask } from "./closeTemplateTasksDrawer.types";
import { TaskLinkFields } from "./templateTaskDrawer/TaskLinkFields";
import { TemplateTaskDependenciesSection } from "./templateTaskDrawer/TemplateTaskDependenciesSection";
import { useTaskLinkState } from "./templateTaskDrawer/useTaskLinkState";
import { TemplateTaskDetailsSection } from "./templateTaskDrawer/TemplateTaskDetailsSection";
import { TemplateTaskDrawerShell } from "./templateTaskDrawer/TemplateTaskDrawerShell";
import { TemplateTaskPreviewPane } from "./templateTaskDrawer/TemplateTaskPreviewPane";
import { TemplateTaskSections } from "./templateTaskDrawer/TemplateTaskSections";
import { TemplateTaskWorkflowSection } from "./templateTaskDrawer/TemplateTaskWorkflowSection";
import { TemplateTaskDetailsSchema } from "./templateTaskDrawer/templateTaskDetails.schema";
import { useInitialMessageHandler } from "./templateTaskDrawer/useInitialMessageHandler";

const AddTaskFormSchema = TemplateTaskDetailsSchema.extend({
    taskType: z.nativeEnum(TaskType),
});

type AddTaskFormValues = z.infer<typeof AddTaskFormSchema>;

const BLANK_VALUES: AddTaskFormValues = {
    name: "",
    closeStageId: null,
    assigneeRole: null,
    assigneeId: null,
    externalDeadline: null,
    deadlineAnchor: null,
    estimatedTime: null,
    taskType: TaskType.QUESTION,
    initialMessage: null,
};

export interface AddTaskResult {
    localId: string;
    name: string;
    closeStageId: string | null;
    assigneeRole: TaskTemplateAssigneeRole | null;
    assigneeId: number | null;
    externalDeadline: number | null;
    deadlineAnchor: TaskTemplateDeadlineAnchor | null;
    estimatedTime: number | null;
    taskType: TaskType | null;
    initialMessage: string | null;
    viewConfig: Record<string, unknown> | null;
    sourceTemplateId: string | null;
    blockedByLocalIds?: string[];
    relatedLocalIds?: string[];
}

type SubmitLabel = "Add" | "Create";

interface Props {
    open: boolean;
    sortedStages: OrganizationCloseStage[];
    initialCloseStageId?: string | null;
    initialTaskTypeOption?: TaskTypeOption;
    onClose: () => void;
    onAddTask: (task: AddTaskResult) => void | Promise<void>;
    basis: CloseTemplateBasis | null;
    submitLabel?: SubmitLabel;
    orgTaskTemplates?: readonly OrganizationTaskTemplate[];
    linkableTasks?: readonly LocalTask[];
}

interface ContentProps {
    sortedStages: OrganizationCloseStage[];
    initialCloseStageId: string | null;
    initialTaskTypeOption: TaskTypeOption;
    onClose: () => void;
    onAddTask: (task: AddTaskResult) => void | Promise<void>;
    basis: CloseTemplateBasis | null;
    submitLabel: SubmitLabel;
    orgTaskTemplates: readonly OrganizationTaskTemplate[];
    linkableTasks: readonly LocalTask[] | undefined;
}

const AddCloseTemplateTaskDrawerContent = ({
    sortedStages,
    initialCloseStageId,
    initialTaskTypeOption,
    onClose,
    onAddTask,
    basis,
    submitLabel,
    orgTaskTemplates,
    linkableTasks,
}: ContentProps) => {
    const links = useTaskLinkState(null, linkableTasks);
    const initialState = useMemo(() => {
        const defaults = getTaskTypeDefaults(initialTaskTypeOption);
        return {
            defaults,
            viewConfig:
                createDefaultViewConfigForTaskTypeOption({
                    option: initialTaskTypeOption,
                    basis,
                }) ?? null,
        };
    }, [initialTaskTypeOption, basis]);
    const [viewConfig, setViewConfig] = useState<Record<
        string,
        unknown
    > | null>(initialState.viewConfig);
    const [transactionsViewType, setTransactionsViewType] =
        useState<TransactionsViewType>(DEFAULT_TRANSACTIONS_VIEW_TYPE);
    const [taskTypeOption, setTaskTypeOption] = useState<TaskTypeOption>(
        initialTaskTypeOption,
    );
    const [sourceTemplateId, setSourceTemplateId] = useState<string | null>(
        null,
    );

    /**
     * Tracks the name and initialMessage values that were last auto-populated
     * when the task type changed. Used to determine whether it is safe to
     * override the current values with new defaults (i.e. the user has not
     * manually edited them).
     */
    const lastAppliedDefaults = useRef<{
        name: string | null;
        initialMessage: string | null;
    }>(initialState.defaults);

    /**
     * Incrementing key used to force a remount of DescriptionField when a
     * default initialMessage is applied programmatically. SlateEditor treats
     * its `value` prop as an initial value only, so a key bump is required
     * to reflect the new content.
     */
    const [descriptionKey, setDescriptionKey] = useState(0);

    const nameInputRef = useRef<HTMLInputElement>(null);
    useAutofocus(nameInputRef);

    const formik = useFormik<AddTaskFormValues>({
        initialValues: {
            ...BLANK_VALUES,
            closeStageId: initialCloseStageId,
            taskType: resolveTaskTypeFromOption(initialTaskTypeOption),
            name: initialState.defaults.name ?? "",
            initialMessage: initialState.defaults.initialMessage,
        },
        validate: validateFormikWithZodSchema(AddTaskFormSchema),
        onSubmit: async (values, { resetForm }) => {
            if (
                values.taskType === TaskType.TRANSACTION_REVIEW &&
                readReviewFieldsFromViewConfig(viewConfig).length === 0
            ) {
                return;
            }

            try {
                await onAddTask({
                    localId: crypto.randomUUID(),
                    name: values.name.trim(),
                    closeStageId: values.closeStageId,
                    assigneeRole: values.assigneeRole,
                    assigneeId: values.assigneeId ?? null,
                    externalDeadline: values.externalDeadline,
                    deadlineAnchor: values.deadlineAnchor,
                    estimatedTime: values.estimatedTime,
                    taskType: values.taskType,
                    initialMessage: values.initialMessage,
                    viewConfig:
                        values.taskType === TaskType.TRANSACTIONS
                            ? withTransactionsViewType(
                                  viewConfig,
                                  transactionsViewType,
                              )
                            : viewConfig,
                    sourceTemplateId,
                    ...(links.isEnabled
                        ? {
                              blockedByLocalIds: links.blockedByLocalIds,
                              relatedLocalIds: links.relatedLocalIds,
                          }
                        : {}),
                });
            } catch {
                return;
            }
            resetForm();
            setViewConfig(initialState.viewConfig);
            setSourceTemplateId(null);
            setTransactionsViewType(DEFAULT_TRANSACTIONS_VIEW_TYPE);
            setTaskTypeOption(initialTaskTypeOption);
            lastAppliedDefaults.current = initialState.defaults;
            setDescriptionKey((k) => k + 1);
        },
    });

    const handleDescriptionChange = useInitialMessageHandler(
        formik.setFieldValue,
    );

    const handleClose = useCallback(() => {
        formik.resetForm();
        setViewConfig(initialState.viewConfig);
        setSourceTemplateId(null);
        setTransactionsViewType(DEFAULT_TRANSACTIONS_VIEW_TYPE);
        setTaskTypeOption(initialTaskTypeOption);
        lastAppliedDefaults.current = initialState.defaults;
        setDescriptionKey((k) => k + 1);
        onClose();
    }, [formik, onClose, initialState, initialTaskTypeOption]);

    const handleTaskTypeOptionChange = useCallback(
        (option: TaskTypeOption) => {
            setTaskTypeOption(option);
            setSourceTemplateId(null);
            const resolvedType = resolveTaskTypeFromOption(option);
            void formik.setFieldValue("taskType", resolvedType);

            const defaults = getTaskTypeDefaults(option);

            // Apply (or clear) the name default if the field is blank or still
            // holds the previously auto-populated value (i.e. user has not
            // edited it). When the new type has no default, the stale
            // auto-populated value is reset back to blank.
            const isNameOverrideable =
                formik.values.name === "" ||
                formik.values.name === lastAppliedDefaults.current.name;
            if (isNameOverrideable) {
                void formik.setFieldValue("name", defaults.name ?? "");
                lastAppliedDefaults.current = {
                    ...lastAppliedDefaults.current,
                    name: defaults.name,
                };
            }

            // Apply (or clear) the initialMessage default under the same
            // condition. Because SlateEditor treats `value` as an initial
            // value, bump a key to force DescriptionField to remount — but only
            // when the value actually changes, to avoid needlessly resetting
            // editor state (cursor/selection) on a no-op round-trip.
            const isMessageOverrideable =
                formik.values.initialMessage === null ||
                formik.values.initialMessage ===
                    lastAppliedDefaults.current.initialMessage;
            if (
                isMessageOverrideable &&
                formik.values.initialMessage !== defaults.initialMessage
            ) {
                void formik.setFieldValue(
                    "initialMessage",
                    defaults.initialMessage,
                );
                setDescriptionKey((k) => k + 1);
                lastAppliedDefaults.current = {
                    ...lastAppliedDefaults.current,
                    initialMessage: defaults.initialMessage,
                };
            }
        },
        [formik],
    );

    const handleOrgTaskTemplateSelect = useCallback(
        (template: OrganizationTaskTemplate) => {
            const defaults = toDrawerDefaultsFromOrgTaskTemplate({
                template,
                basis,
            });
            const { formValues } = defaults;
            void formik.setValues({
                ...formValues,
                taskType: defaults.taskType,
                closeStageId:
                    formValues.closeStageId ?? formik.values.closeStageId,
            });
            setSourceTemplateId(defaults.sourceTemplateId);
            setTaskTypeOption(defaults.taskTypeOption);
            setViewConfig(defaults.viewConfig);
            setTransactionsViewType(defaults.transactionsViewType);
            lastAppliedDefaults.current = {
                name: formValues.name,
                initialMessage: formValues.initialMessage,
            };
            setDescriptionKey((k) => k + 1);
        },
        [formik, basis],
    );

    const hasMissingReviewFields =
        formik.values.taskType === TaskType.TRANSACTION_REVIEW &&
        readReviewFieldsFromViewConfig(viewConfig).length === 0;

    return (
        <FormikProvider value={formik}>
            <TemplateTaskDrawerShell
                open
                title="Add task"
                submitLabel={submitLabel}
                taskType={formik.values.taskType}
                onClose={handleClose}
                onSubmit={formik.handleSubmit}
                isSubmitDisabled={
                    !formik.values.name.trim() ||
                    hasMissingReviewFields ||
                    formik.isSubmitting
                }
                preview={
                    <TemplateTaskPreviewPane
                        taskType={formik.values.taskType}
                        previewKey={String(taskTypeOption)}
                        viewConfig={viewConfig}
                        onViewConfigChange={setViewConfig}
                        basis={basis}
                    />
                }
            >
                <TaskDrawerHeaderFields<AddTaskFormValues>
                    titleName="name"
                    titlePlaceholder="Task name"
                    titleRef={nameInputRef}
                    description={formik.values.initialMessage}
                    onDescriptionChange={handleDescriptionChange}
                    descriptionResetKey={descriptionKey}
                    renderWorkflowPicker={(pickerProps) => (
                        <TaskTypePicker
                            taskType={taskTypeOption}
                            viewConfig={viewConfig}
                            onTaskTypeChange={handleTaskTypeOptionChange}
                            onViewConfigChange={setViewConfig}
                            basis={basis}
                            orgTaskTemplates={orgTaskTemplates}
                            onOrgTaskTemplateSelect={
                                handleOrgTaskTemplateSelect
                            }
                            {...pickerProps}
                        />
                    )}
                />
                <TemplateTaskSections>
                    <TemplateTaskDetailsSection sortedStages={sortedStages} />
                    {links.isEnabled && (
                        <TemplateTaskDependenciesSection>
                            <TaskLinkFields
                                options={links.options}
                                blockedByLocalIds={links.blockedByLocalIds}
                                relatedLocalIds={links.relatedLocalIds}
                                cyclicLocalIds={links.cyclicLocalIds}
                                onBlockedByChange={links.setBlockedByLocalIds}
                                onRelatedChange={links.setRelatedLocalIds}
                            />
                        </TemplateTaskDependenciesSection>
                    )}
                    <TemplateTaskWorkflowSection>
                        <TaskTypeField
                            htmlIdPrefix="addTask"
                            taskType={taskTypeOption}
                            viewConfig={viewConfig}
                            onTaskTypeChange={handleTaskTypeOptionChange}
                            onViewConfigChange={setViewConfig}
                            basis={basis}
                            transactionsViewType={transactionsViewType}
                            onTransactionsViewTypeChange={
                                setTransactionsViewType
                            }
                            orgTaskTemplates={orgTaskTemplates}
                            onOrgTaskTemplateSelect={
                                handleOrgTaskTemplateSelect
                            }
                        />
                    </TemplateTaskWorkflowSection>
                </TemplateTaskSections>
            </TemplateTaskDrawerShell>
        </FormikProvider>
    );
};

export const AddCloseTemplateTaskDrawer = ({
    open,
    sortedStages,
    initialCloseStageId,
    initialTaskTypeOption = TaskType.QUESTION,
    onClose,
    onAddTask,
    basis,
    submitLabel = "Add",
    orgTaskTemplates = [],
    linkableTasks,
}: Props) => {
    if (!open) {
        return null;
    }

    return (
        <AddCloseTemplateTaskDrawerContent
            key={`${initialCloseStageId ?? "none"}:${initialTaskTypeOption}`}
            sortedStages={sortedStages}
            initialCloseStageId={initialCloseStageId ?? null}
            initialTaskTypeOption={initialTaskTypeOption}
            onClose={onClose}
            onAddTask={onAddTask}
            basis={basis}
            submitLabel={submitLabel}
            orgTaskTemplates={orgTaskTemplates}
            linkableTasks={linkableTasks}
        />
    );
};
